import { Extension, isTextSelection } from '@tiptap/core'
import { Node } from '@tiptap/pm/model'
import { Plugin, PluginKey, Selection as PmSelection } from '@tiptap/pm/state'
import { CellSelection } from '@tiptap/pm/tables'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

function nodesBetweenRev(
  from: number,
  to: number,
  f: (
    node: Node,
    pos: number,
    parent: Node | null,
    index: number,
  ) => boolean | void,
  nodeStart = 0,
  parent: Node,
) {
  for (
    let i = parent.content.content.length - 1, pos = parent.content.size;
    i >= 0;
    i--
  ) {
    let child = parent.content.content[i]
    let start = pos - child.nodeSize

    // Check if this node overlaps with our range
    if (start < to && pos > from) {
      // Call the callback, and if it doesn't return false and the node has content, recurse
      if (
        f(child, nodeStart + start, parent || null, i) !== false &&
        child.content.size
      ) {
        // Calculate the relative positions for the child
        let childStart = start + 1
        nodesBetweenRev(
          Math.max(0, from - childStart),
          Math.min(child.content.size, to - childStart),
          f,
          nodeStart + childStart,
          child,
        )
      }
    }

    pos = start
  }
}

/**
 * Finds the nearest text position to a given NodeSelection in a ProseMirror document
 */
export function findStartTextPos(
  doc: Node,
  selection: PmSelection,
): number | null {
  let startPos: number | null = null

  const isInvalidPos = (node: Node, pos: number) => {
    if (node.isText) startPos = pos
    return !node.isText
  }

  doc.nodesBetween(selection.from, selection.to, isInvalidPos)

  return Math.max(startPos ?? selection.from, selection.from)
}

/**
 * Finds the nearest text position to a given NodeSelection in a ProseMirror document
 */
export function findEndTextPos(
  doc: Node,
  selection: PmSelection,
): number | null {
  let endPos: number | null = null

  const isInvalidPos = (node: Node, pos: number) => {
    if (node.isText) endPos = pos + node.nodeSize
    return !node.isText
  }

  nodesBetweenRev(selection.from, selection.to, isInvalidPos, 0, doc)

  return Math.min(endPos ?? selection.to, selection.to)
}

export const Selection = Extension.create({
  name: 'selection',

  addProseMirrorPlugins() {
    const { editor } = this

    return [
      new Plugin({
        key: new PluginKey('selection'),
        props: {
          decorations(state) {
            // If the selection is empty or the editor isn't in the right state, do nothing
            if (
              state.selection.empty ||
              !editor.isEditable ||
              editor.isFocused
            ) {
              return null
            }

            const decorations = []
            const { selection } = state

            // Check if the selection is for table cells
            if (selection instanceof CellSelection) {
              // Iterate over each selected cell
              selection.forEachCell((node, pos) => {
                // Apply a decoration to the entire cell node
                decorations.push(
                  Decoration.node(pos, pos + node.nodeSize, {
                    class: 'selection-bg',
                  }),
                )
              })
            }
            // Otherwise, handle it as a standard text selection
            else {
              decorations.push(
                // Apply an inline decoration to the selected text range
                Decoration.inline(selection.from, selection.to, {
                  class: 'selection-bg',
                }),
              )
            }

            return DecorationSet.create(state.doc, decorations)
          },
        },
      }),
    ]
  },
})
